import { decode, fromUint8Array, toUint8Array } from 'js-base64';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useRef } from 'react';
import { useState } from 'react';
import ePub from 'epubjs';

function usePersistentState(defaultValue, key) {
	const [value, setValue] = useState(() => {
		const stickyValue = window.localStorage.getItem(key);
		return stickyValue !== null ? JSON.parse(stickyValue) : defaultValue;
	});

	function setPersistentValue(fn) {
		const nextValue = fn(value);
		window.localStorage.setItem(key, JSON.stringify(nextValue));
		setValue(nextValue);
	}

	return [value, setPersistentValue];
}

async function saveFileWithFileSystemAccess(filename, content, mimeType) {
	try {
		// Show a file save dialog to the user
		const handle = await window.showSaveFilePicker({
			suggestedName: filename,
			types: [
				{
					description: 'ePub Files',
					accept: {
						[mimeType]: ['.epub'],
					},
				},
			],
		});

		// Create a WritableStream to write to the file
		const writableStream = await handle.createWritable();

		// Write the content to the file
		await writableStream.write(content);

		// Close the file
		await writableStream.close();
	} catch (error) {
		if (error.name === 'AbortError') {
			console.log('File save aborted by the user.');
		} else {
			console.error('Error saving file:', error);
		}
	}
}

function History({ items, open }) {
	return (
		<ul role="list" className="grid grid-cols-1 gap-x-6 gap-y-6 lg:grid-cols-3 xl:gap-x-8">
			{items.map((item) => (
				<a
					key={item.filename}
					onClick={(e) => { e.preventDefault(); open(item); }}
					href="#"
					className="group"
				>
					<li className="overflow-hidden rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:border-white/30 transition-all cursor-pointer">
						<div className="px-6 py-5">
							<div className="flex items-start justify-between">
								<div className="flex-1 min-w-0">
									<h3 className="text-base font-semibold text-white group-hover:text-indigo-200 transition-colors truncate">
										{item.title}
									</h3>
									<p className="mt-2 text-sm text-indigo-200 truncate">
										{item.url}
									</p>
								</div>
								<svg className="ml-4 w-5 h-5 text-indigo-300 group-hover:text-white transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
								</svg>
							</div>
						</div>
					</li>
				</a>
			))}
		</ul>
	);
}

function App() {
	const [loading, setLoading] = useState(false);
	const [url, setUrl] = useState('');
	const [epub, setEpub] = useState(null);
	const [epubTitle, setEpubTitle] = useState(null);
	const [epubFilename, setEpubFilename] = useState(null);
	const [error, setError] = useState(null);
	// const bookRef = useRef(null);

	const [history, setHistory] = usePersistentState([], 'history');

	async function submit(e) {
		e.preventDefault();

		if (!url || url.trim() === '') {
			setError('Please enter a valid URL');
			return;
		}

		try {
			new URL(url);
		} catch (err) {
			setError('Please enter a valid URL (e.g., https://example.com/article)');
			return;
		}

		convert(url);
	}

	async function convert(url) {
		setLoading(true);
		setError(null);

		try {
			const res = await fetch('/', { method: 'POST', body: url });

			if (!res.ok) {
				let errorMsg = 'Failed to convert this URL';
				if (res.status === 404) {
					errorMsg = 'The URL could not be found. Please check the URL and try again.';
				} else if (res.status === 500) {
					errorMsg = 'Server error while converting. The webpage might not be compatible.';
				} else if (res.status >= 400 && res.status < 500) {
					errorMsg = 'Unable to convert this webpage. It might be protected or require authentication.';
				}
				throw new Error(errorMsg);
			}

			const titleHeader = res.headers.get('epub-title');
			const filenameHeader = res.headers.get('epub-filename');

			if (!titleHeader || !filenameHeader) {
				throw new Error('The webpage could not be converted properly. Please try a different URL.');
			}

			const title = decode(titleHeader);
			const filename = decode(filenameHeader);

			setEpubTitle(title);
			setEpubFilename(filename);

			const epub = await res.arrayBuffer();

			if (!epub || epub.byteLength === 0) {
				throw new Error('The conversion produced an invalid file. Please try again.');
			}

			setEpub(epub);
			setHistory((history) => {
				history.push({ url, title, filename });
				return history;
			});

			setLoading(false);
		} catch (err) {
			console.error('Conversion error:', err);

			// Handle network errors
			if (err.name === 'TypeError' && err.message.includes('fetch')) {
				setError('Network error. Please check your internet connection and try again.');
			} else {
				setError(err.message || 'Failed to convert the URL. Please check the URL and try again.');
			}

			setLoading(false);
			setEpub(null);
		}

		// const book = ePub(epub);
		// const rendered = await book.renderTo(bookRef.current, { flow: 'scrolled-doc', width: '100%', height: '90%' });
		// const displayed = await rendered.display();
		// console.log({ displayed });
	}

	async function openHistoryItem(item) {
        await convert(item.url)
	}

	async function download() {
		await saveFileWithFileSystemAccess(epubFilename, epub, 'application/epub+zip');
		setEpub(null);
	}

	return (
		<>
			<Header />

			<Dialog open={epub !== null || loading === true || error !== null} onClose={() => { setEpub(null); setError(null); }} className="relative z-10">
				<DialogBackdrop
					transition
					className="fixed inset-0 bg-gray-500/75 transition-opacity duration-500 ease-in-out data-closed:opacity-0"
				/>

				<div className="fixed inset-0 flex items-center justify-center p-4">
					<DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full max-w-lg">
						<div className="absolute right-0 top-0 pr-4 pt-4">
							<button
								type="button"
								onClick={() => { setEpub(null); setError(null); }}
								className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-2 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
							>
								<span className="sr-only">Close</span>
								<XMarkIcon aria-hidden="true" className="size-6" />
							</button>
						</div>

						<div className="text-center">
							{loading ? (
								<>
									<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-indigo-100">
										<ArrowPathIcon aria-hidden="true" className="h-8 w-8 text-indigo-600 animate-spin" />
									</div>
									<DialogTitle as="h3" className="mt-4 text-lg font-semibold text-gray-900">
										Converting to ePUB...
									</DialogTitle>
									<p className="mt-2 text-sm text-gray-500">
										This should only take a moment
									</p>
								</>
							) : error ? (
								<>
									<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
										<XMarkIcon aria-hidden="true" className="h-8 w-8 text-red-600" />
									</div>
									<DialogTitle as="h3" className="mt-4 text-lg font-semibold text-gray-900">
										Conversion Failed
									</DialogTitle>
									<p className="mt-2 text-sm text-gray-500">
										{error}
									</p>
									<div className="mt-6">
										<button
											type="button"
											onClick={() => { setError(null); setEpub(null); }}
											className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
										>
											Try Again
										</button>
									</div>
								</>
							) : epub ? (
								<>
									<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
										<CheckIcon aria-hidden="true" className="h-8 w-8 text-green-600" />
									</div>
									<DialogTitle as="h3" className="mt-4 text-lg font-semibold text-gray-900">
										Ready to Download
									</DialogTitle>
									<p className="mt-2 text-sm text-gray-500 truncate px-4">
										{epubTitle}
									</p>
									<div className="mt-6">
										<button
											type="button"
											onClick={download}
											className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
										>
											Download {epubFilename}
										</button>
									</div>
								</>
							) : null}
						</div>
					</DialogPanel>
				</div>
			</Dialog>

			<div className="content h-full md:h-screen px-4">
				<div className="flex w-full pt-32 md:pt-48">
					<div className="w-full container-lg mx-auto">
						<div className="py-16 sm:py-24 lg:py-32">
							<div className="text-center mb-8">
								<div className="inline-flex items-center rounded-full bg-indigo-500/10 px-4 py-1.5 text-sm font-medium text-white border border-indigo-400/30 mb-6">
									Free, fast, and works offline
								</div>
								<h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white mb-6">
									Turn Any Article Into an eBook
								</h1>
								<p className="text-xl text-indigo-100 max-w-3xl mx-auto mb-4">
									Save articles, blog posts, and web pages as ePUB files. Read them anywhere, anytime on your e-reader, tablet, or phone.
								</p>
								<p className="text-sm text-indigo-200 max-w-2xl mx-auto">
									Perfect for Kindle, Kobo, Apple Books, and any ePUB-compatible device
								</p>
							</div>

							<form className="mt-12 max-w-2xl mx-auto">
								<div className="flex flex-col md:flex-row gap-x-4 gap-y-4 shadow-2xl">
									<input
										type="url"
										required
										placeholder="https://example.com/article"
										className="flex-auto rounded-md bg-white px-4 py-3.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 w-full md:w-auto"
										value={url}
										onChange={(e) => setUrl(e.target.value)}
									/>
									<button
										type="submit"
										className="flex-none rounded-md bg-indigo-600 px-6 py-3.5 text-base font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-full md:w-auto transition-colors"
										onClick={submit}
									>
										Convert to ePub
									</button>
								</div>
								<p className="mt-4 text-sm text-indigo-200 text-center">
									No sign-up required. Your privacy is protected.
								</p>
							</form>

							<div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
								<div className="text-center">
									<div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
										<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
										</svg>
									</div>
									<h3 className="text-lg font-semibold text-white mb-2">Read Offline</h3>
									<p className="text-indigo-200 text-sm">Download once, read forever. No internet needed.</p>
								</div>

								<div className="text-center">
									<div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
										<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
										</svg>
									</div>
									<h3 className="text-lg font-semibold text-white mb-2">Clean & Ad-Free</h3>
									<p className="text-indigo-200 text-sm">Pure reading experience without distractions.</p>
								</div>

								<div className="text-center">
									<div className="w-12 h-12 rounded-lg bg-indigo-500/20 flex items-center justify-center mx-auto mb-4">
										<svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
										</svg>
									</div>
									<h3 className="text-lg font-semibold text-white mb-2">Works Everywhere</h3>
									<p className="text-indigo-200 text-sm">Compatible with all ePUB readers and devices.</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				{history.length > 0 ? (
					<div className="container-lg mx-auto mt-24 pb-24">
						<div className="pb-8">
							<h2 className="text-3xl font-bold tracking-tight text-white">Your Recent Conversions</h2>
							<p className="mt-2 text-indigo-200">Quick access to your previously converted articles</p>
						</div>

						<History items={history} open={openHistoryItem} />
					</div>
				) : (
					<div className="container-lg mx-auto mt-24 pb-24">
						<div className="text-center py-12">
							<h2 className="text-2xl font-bold tracking-tight text-white mb-4">How It Works</h2>
							<div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8 max-w-4xl mx-auto">
								<div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
									<div className="text-3xl font-bold text-indigo-300 mb-3">1</div>
									<h3 className="text-lg font-semibold text-white mb-2">Paste URL</h3>
									<p className="text-indigo-200 text-sm">Copy the link to any article or blog post you want to save</p>
								</div>
								<div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
									<div className="text-3xl font-bold text-indigo-300 mb-3">2</div>
									<h3 className="text-lg font-semibold text-white mb-2">Convert</h3>
									<p className="text-indigo-200 text-sm">We extract and format the content into a beautiful ePUB</p>
								</div>
								<div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
									<div className="text-3xl font-bold text-indigo-300 mb-3">3</div>
									<h3 className="text-lg font-semibold text-white mb-2">Enjoy</h3>
									<p className="text-indigo-200 text-sm">Download and read on any device, anywhere, anytime</p>
								</div>
							</div>
						</div>
					</div>
				)}

				{/*
<div className="container mx-auto mt-24">
					<div className="pb-5">
						<h2 className="text-2xl font-bold tracking-tight text-white">Featured books</h2>
						<p className="mt-2 max-w-4xl text-sm text-white">
        Books from popular blogs, articles etc.

						</p>
					</div>

					<History items={history} open={openHistoryItem} />
				</div>
            */}
			</div>

			<svg
				className="waves hidden md:block"
				xmlns="http://www.w3.org/2000/svg"
				xmlnsXlink="http://www.w3.org/1999/xlink"
				viewBox="0 24 150 28"
				preserveAspectRatio="none"
				shape-rendering="auto"
			>
				<defs>
					<path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
				</defs>
				<g className="parallax">
					<use xlinkHref="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.7" />
					<use xlinkHref="#gentle-wave" x="48" y="3" fill="rgba(255,255,255,0.5)" />
					<use xlinkHref="#gentle-wave" x="48" y="5" fill="rgba(255,255,255,0.3)" />
					<use xlinkHref="#gentle-wave" x="48" y="7" fill="#fff" />
				</g>
			</svg>

			<footer className="bg-white mt-auto">
				<div className="container-lg mx-auto px-4 py-12">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
						<div>
							<h3 className="text-lg font-semibold text-gray-900 mb-4">Perfect For</h3>
							<ul className="space-y-2 text-sm text-gray-600">
								<li>Long-form articles and essays</li>
								<li>Technical documentation</li>
								<li>Blog posts and tutorials</li>
								<li>News articles and reports</li>
								<li>Research papers</li>
							</ul>
						</div>

						<div>
							<h3 className="text-lg font-semibold text-gray-900 mb-4">Supported Devices</h3>
							<ul className="space-y-2 text-sm text-gray-600">
								<li>Amazon Kindle</li>
								<li>Kobo e-readers</li>
								<li>Apple Books (iPad/iPhone)</li>
								<li>Google Play Books</li>
								<li>Any ePUB reader app</li>
							</ul>
						</div>

						<div>
							<h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
							<p className="text-sm text-gray-600 mb-4">
								A free tool to help you save and read web content offline. No tracking, no ads, no sign-up required.
							</p>
							<a href="mailto:epub@sauleau.com" className="text-sm font-semibold text-indigo-600 hover:text-indigo-500">
								Send feedback →
							</a>
						</div>
					</div>

					<div className="pt-8 border-t border-gray-200 text-center text-sm text-gray-500">
						<p>Made with care for readers everywhere</p>
					</div>
				</div>
			</footer>
		</>
	);
}

function Header() {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	return (
		<header className="bg-white absolute right-0">
			<nav aria-label="Global" className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
				<a href="mailto:epub@sauleau.com" className="text-sm/6 font-semibold text-gray-900">
					Send feedback <span aria-hidden="true">&rarr;</span>
				</a>
			</nav>
		</header>
	);
}

export default App;
